import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { sermonsApi, announcementsApi, eventsApi, prayerApi, contactApi, newsletterApi } from '@/lib/api';
import { Video, Bell, Calendar, Heart, MessageSquare, Mail, ArrowRight } from 'lucide-react';

const AdminHome = () => {
  const [stats, setStats] = useState({
    sermons: 0,
    announcements: 0,
    events: 0,
    prayerRequests: 0,
    messages: 0,
    subscribers: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [sermons, announcements, events, prayers, messages, subscribers] = await Promise.all([
        sermonsApi.getAll().catch(() => []),
        announcementsApi.getAll(false).catch(() => []),
        eventsApi.getAll(false).catch(() => []),
        prayerApi.getAll(false).catch(() => []),
        contactApi.getMessages().catch(() => []),
        newsletterApi.getSubscribers().catch(() => [])
      ]);

      setStats({
        sermons: sermons.length,
        announcements: announcements.length,
        events: events.length,
        prayerRequests: prayers.filter(p => !p.is_approved).length,
        messages: messages.filter(m => !m.is_read).length,
        subscribers: subscribers.length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Sermons', value: stats.sermons, icon: Video, href: '/admin/sermons', color: 'bg-blue-500' },
    { title: 'Announcements', value: stats.announcements, icon: Bell, href: '/admin/announcements', color: 'bg-amber-500' },
    { title: 'Events', value: stats.events, icon: Calendar, href: '/admin/events', color: 'bg-green-500' },
    { title: 'Prayer Requests', value: stats.prayerRequests, icon: Heart, href: '/admin/prayer-requests', color: 'bg-red-500', subtitle: 'pending approval' },
    { title: 'Unread Messages', value: stats.messages, icon: MessageSquare, href: '/admin/messages', color: 'bg-purple-500' },
    { title: 'Subscribers', value: stats.subscribers, icon: Mail, href: '/admin/newsletter', color: 'bg-teal-500' },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-stone-800">Dashboard</h1>
        <p className="text-stone-600">Welcome to the St. John's UCC admin dashboard</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-12 w-12 bg-stone-200 rounded-lg mb-4"></div>
                <div className="h-8 w-16 bg-stone-200 rounded mb-2"></div>
                <div className="h-4 w-24 bg-stone-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((card) => (
            <Link key={card.title} to={card.href}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className={`h-12 w-12 ${card.color} rounded-lg flex items-center justify-center mb-4`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-stone-800 mb-1">{card.value}</div>
                  <div className="text-stone-600">{card.title}</div>
                  {card.subtitle && (
                    <div className="text-sm text-stone-500">{card.subtitle}</div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-stone-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/sermons">
            <Card className="hover:bg-stone-50 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-stone-700">Add New Sermon</span>
                <ArrowRight className="h-4 w-4 text-amber-700" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/announcements">
            <Card className="hover:bg-stone-50 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-stone-700">Create Announcement</span>
                <ArrowRight className="h-4 w-4 text-amber-700" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/events">
            <Card className="hover:bg-stone-50 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-stone-700">Add Event</span>
                <ArrowRight className="h-4 w-4 text-amber-700" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/admin/galleries">
            <Card className="hover:bg-stone-50 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-stone-700">Create Gallery</span>
                <ArrowRight className="h-4 w-4 text-amber-700" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
